import { Doughnut } from "react-chartjs-2";
import ChartCard from "./ChartsCard";
import { doughnutOptions } from "./ChartOptions/doughnutChartOptions";
import { ChartOptions } from "chart.js";
import { useContactEnquiry } from "../../hooks/useContactEnquiry";
import { useProductEnquiry } from "../../hooks/useProductEnquiry";
import { useServiceEnquiry } from "../../hooks/useServiceEnquiry";
import { useEffect, useState } from "react";

export default function ProductContributionChart() {
  const { totalCount: contactTotal, isLoading: contactLoading } = useContactEnquiry();
  const { totalCount: productTotal, isLoading: productLoading } = useProductEnquiry();
  const { totalCount: serviceTotal, isLoading: serviceLoading } = useServiceEnquiry();

  const [chartData, setChartData] = useState({
    contact: 0,
    product: 0,
    service: 0,
    total: 0,
  });

  const [isLoading, setIsLoading] = useState(true);

  // Calculate percentages when counts change
  useEffect(() => {
    if (!contactLoading && !productLoading && !serviceLoading) {
      const total = contactTotal + productTotal + serviceTotal;
      
      setChartData({
        contact: contactTotal,
        product: productTotal,
        service: serviceTotal,
        total: total,
      });
      setIsLoading(false);
    }
  }, [contactTotal, productTotal, serviceTotal, contactLoading, productLoading, serviceLoading]);

  // Calculate percentages
  const calculatePercentage = (value: number) => {
    if (chartData.total === 0) return 0;
    return Math.round((value / chartData.total) * 100);
  };

  const productContributionData = {
    labels: [
      "Contact Us",
      "Products",
      "Services",
    ],
    datasets: [
      {
        label: "Enquiry Counts",
        data: [
          calculatePercentage(chartData.contact),
          calculatePercentage(chartData.product),
          calculatePercentage(chartData.service),
        ],
        backgroundColor: [
          "rgba(220, 38, 38, 0.85)",   // Red
          "rgba(37, 99, 235, 0.85)",   // Blue
          "rgba(16, 185, 129, 0.85)",  // Green
        ],
        borderWidth: 0,
        hoverOffset: 8,
        hoverBackgroundColor: [
          "rgba(220, 38, 38, 1)",
          "rgba(37, 99, 235, 1)",
          "rgba(16, 185, 129, 1)",
        ],
      },
    ],
  };

  // Custom tooltip to show both count and percentage
  const customDoughnutOptions: ChartOptions<"doughnut"> = {
    ...doughnutOptions,
    responsive: true,
    maintainAspectRatio: false,
    cutout: "65%",
    plugins: {
      ...doughnutOptions.plugins,
      legend: {
        position: "bottom" as const,
        labels: {
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle',
          font: {
            size: 12,
            family: "'Roboto', sans-serif",
          },
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = context.parsed;
            const counts = [chartData.contact, chartData.product, chartData.service];
            const count = counts[context.dataIndex];
            
            return `${label}: ${count} enquiries`;
          },
        },
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        padding: 12,
        displayColors: true,
      },
    },
  };

  if (isLoading) {
    return (
      <ChartCard title="Enquiry Counts">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <svg className="animate-spin h-8 w-8 text-red-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <p className="text-gray-600">Loading enquiry data...</p>
          </div>
        </div>
      </ChartCard>
    );
  }

  return (
    <ChartCard title="Enquiry Counts">
      {/* Summary stats */}
      {/* <div className="mb-4">
        <div className="grid grid-cols-3 gap-4 mb-2">
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{chartData.contact.toLocaleString()}</div>
            <div className="text-xs text-gray-600">Contact</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{chartData.product.toLocaleString()}</div>
            <div className="text-xs text-gray-600">Product</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{chartData.service.toLocaleString()}</div>
            <div className="text-xs text-gray-600">Service</div>
          </div>
        </div>
        <div className="text-center pt-2 border-t">
          <div className="text-sm font-semibold text-gray-700">
            Total Enquiries: <span className="text-lg text-red-600">{chartData.total.toLocaleString()}</span>
          </div>
        </div>
      </div> */}

      {/* Chart */}
      <div className="h-full">
        <Doughnut
          data={productContributionData}
          options={customDoughnutOptions}
        />
      </div>

    </ChartCard>
  );
}